// Messaging Platforms Store Module
import { createBaseModule } from './base'
import { messagingPlatformsApi, slackWebhooksApi } from '@/services/api'
import { generateWebhookUrl } from '../../config/api'

// Custom getters for messaging platforms
const customGetters = {
  // Helper to group platforms by type for UI display
  platformsByType: state => {
    const grouped = {}
    state.items.forEach(platform => {
      const type = platform.platform
      if (!grouped[type]) {
        grouped[type] = []
      }
      grouped[type].push(platform)
    })
    return grouped
  },
  
  // Get platform by instance ID and type
  getPlatformByInstance: state => (platform, instanceId) => 
    state.items.find(p => p.platform === platform && p.instance_id === instanceId),
  
  // Get platforms by type
  getPlatformsByType: state => platform => 
    state.items.filter(p => p.platform === platform)
}

function generateMongoId() {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomPart = 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () =>
    (Math.random() * 16 | 0).toString(16)
  );
  return timestamp + randomPart;
}


// Custom actions for messaging platforms
const customActions = {
  // Override fetchAll to use the correct endpoint
  async fetchAll({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const data = await messagingPlatformsApi.getPlatforms()
      commit('SET_ITEMS', data)
      
      return data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch messaging platforms')
      throw error
    }
  },

  // Custom create action for platforms
  async create({ commit, dispatch }, platformData) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      let newInstanceId = platformData.instance_id || generateMongoId()
      
      // Handle Slack webhooks differently
      if (String(platformData.platform).toLowerCase() === 'slack') {
        // Use Slack webhook API
        const slackPayload = {
          webhook_url: platformData.config?.webhook_url || platformData.webhook_url,
          channel: platformData.config?.default_channel || platformData.config?.channel,
          name: platformData.name || 'Slack Integration'
        }
        
        console.log('Creating Slack webhook with payload:', slackPayload)
        console.log('Platform data received:', platformData)
        const data = await slackWebhooksApi.addWebhook(slackPayload)
        commit('ADD_ITEM', data)
        
        dispatch('showSuccess', 'Slack webhook created successfully', { root: true })
        return data
      } else {
        // Use traditional messaging platform initialization for other platforms
        const payload = {
          platform: String(platformData.platform),
          instance_id: newInstanceId,
          name: platformData.name || platformData.platform_name || platformData.platform,
          config: platformData.config || {},
          allowed_agents: [...platformData.allowed_agents] || []
        }
        
        // Only generate system webhook URL for non-Slack platforms
        // For Slack, the user provides their own webhook URL
        if (String(platformData.platform).toLowerCase() !== 'slack') {
          payload.config.webhook_url = generateWebhookUrl(payload.platform, newInstanceId)
        }
        
        const data = await messagingPlatformsApi.initialize(payload)
        commit('ADD_ITEM', data)
        
        dispatch('showSuccess', 'Messaging platform created successfully', { root: true })
        return data
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create messaging platform')
      dispatch('showError', error.message || 'Failed to create messaging platform', { root: true })
      throw error
    }
  },

  // Custom update action for platforms
  async update({ commit, dispatch }, { platform, instanceId, config, allowed_agents }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      // Build the update payload - ensure platform is a string
      const payload = {
        platform: String(platform),
        instance_id: instanceId || null,
        name: config.name,
        enabled: config.enabled,
        allowed_agents: allowed_agents || [], // Add allowed_agents at top level
        config: {
          ...config,
          webhook_url: config.webhook_url
        }
      }
      
      const data = await messagingPlatformsApi.updateConfig(payload)
      commit('UPDATE_ITEM', { id: instanceId || platform, updates: data })
      
      dispatch('showSuccess', 'Messaging platform updated successfully', { root: true })
      
      return data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update messaging platform')
      dispatch('showError', error.message || 'Failed to update messaging platform', { root: true })
      throw error
    }
  },

  // Custom delete action for platforms
  async delete({ commit, dispatch }, { platform, instanceId }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const platformString = String(platform)
      
      if (instanceId) {
        await messagingPlatformsApi.deleteConfigByInstance(platformString, instanceId)
        commit('REMOVE_ITEM', instanceId)
      } else {
        await messagingPlatformsApi.deleteConfig(platformString)
        commit('REMOVE_ITEM', platformString)
      }
      
      dispatch('showSuccess', 'Messaging platform deleted successfully', { root: true })
      
      return true
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete messaging platform')
      dispatch('showError', error.message || 'Failed to delete messaging platform', { root: true })
      throw error
    }
  },

  // Test platform connection
  async testConnection({ commit, dispatch }, { platform, instanceId }) {
    try {
      commit('SET_LOADING', true)
      
      const testPayload = instanceId ? { platform, instance_id: instanceId } : { platform }
      const result = await messagingPlatformsApi.testConnection(testPayload)
      
      dispatch('showSuccess', 'Platform connection test successful', { root: true })
      
      return result
    } catch (error) {
      commit('SET_ERROR', error.message || 'Platform connection test failed')
      dispatch('showError', error.message || 'Platform connection test failed', { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get platform status
  async getStatus({ commit }, params = {}) {
    try {
      const status = await messagingPlatformsApi.getStatus(params)
      return status
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to get platform status')
      throw error
    }
  }
}

// Custom mutations for handling different ID fields
const customMutations = {
  UPDATE_ITEM(state, { id, updates }) {
    // Find platform by id, instance_id, or platform name
    const index = state.items.findIndex(p => {
      return p.id === id || 
             p.instance_id === id || 
             p.platform === id ||
             String(p.id) === String(id) ||
             String(p.instance_id) === String(id) ||
             String(p.platform) === String(id)
    })
    if (index >= 0) {
      state.items.splice(index, 1, { ...state.items[index], ...updates })
    }
    
    // Update current item if it's the same
    if (state.currentItem && (
      state.currentItem.id === id ||
      state.currentItem.instance_id === id ||
      state.currentItem.platform === id
    )) {
      state.currentItem = { ...state.currentItem, ...updates }
    }
    
    state.loading = false
    state.error = null
  },
  
  REMOVE_ITEM(state, id) {
    // Handle removal by instance_id, id, or platform name
    state.items = state.items.filter(p => {
      return p.id !== id && 
             p.instance_id !== id && 
             p.platform !== id &&
             String(p.id) !== String(id) &&
             String(p.instance_id) !== String(id) &&
             String(p.platform) !== String(id)
    })
    
    // Clear current item if it's the one being removed
    if (state.currentItem && (
      state.currentItem.id === id ||
      state.currentItem.instance_id === id ||
      state.currentItem.platform === id
    )) {
      state.currentItem = null
    }
    
    state.loading = false
    state.error = null
  }
}

// Create and export the module
export default createBaseModule('messaging platform', messagingPlatformsApi, {
  customGetters,
  customActions,
  customMutations,
  idField: 'instance_id' // Use instance_id as primary identifier
})
